<template>
  <div>
    <!-- Page header -->
    <CCard class="mb-4 border-0">
      <CCardBody class="p-4">
        <router-link to="/employee/receiptList" class="small d-inline-block mb-2">&larr; My receipts</router-link>
        <CRow class="align-items-center">
          <CCol md="6">
            <h1 class="h4 mb-1 font-weight-bold">{{ obj.id ? obj.companyName || "Receipt" : "New receipt" }}</h1>
            <p class="text-muted mb-0">Upload a photo or PDF, check the details, then save</p>
          </CCol>
          <CCol md="6" class="d-flex flex-wrap justify-content-md-end align-items-center mt-3 mt-md-0">
            <template v-if="obj.id">
              <CButton color="light" class="mr-2 mb-2 mb-md-0" aria-label="Previous receipt" title="Previous receipt" @click="previous">
                <CIcon name="cil-chevron-left" />
              </CButton>
              <CButton color="light" class="mr-2 mb-2 mb-md-0" aria-label="Next receipt" title="Next receipt" @click="next">
                <CIcon name="cil-chevron-right" />
              </CButton>
            </template>
            <CButton color="light" class="mr-2 mb-2 mb-md-0" :disabled="saving" @click="cancel">Cancel</CButton>
            <CButton color="primary" class="mb-2 mb-md-0" :disabled="saving" @click="submit">
              <CSpinner v-if="saving" size="sm" class="mr-1" />
              {{ obj.id ? "Save" : "Add receipt" }}
            </CButton>
          </CCol>
        </CRow>
      </CCardBody>
    </CCard>

    <CRow>
      <CCol lg="8">
        <CCard>
          <CCardHeader><strong>Receipt image</strong></CCardHeader>
          <CCardBody>
            <div v-if="isLoading" class="mb-4">
              <CProgress :value="progressValue" color="primary" />
              <small class="text-muted">Reading your receipt… {{ progressValue }}%</small>
            </div>

            <CRow v-if="obj.documentId">
              <CCol sm="6">
                <CImg :src="billImageUrl" class="mb-3" responsive alt="Uploaded receipt" />
              </CCol>
            </CRow>
            <WidgetsUploadImage
              :billImageUrl="billImageUrl"
              @uploaded="uploaded"
            />
          </CCardBody>
          <CCardFooter>
            <CButton
              color="primary"
              variant="outline"
              @click="extract"
              :disabled="isLoading || !obj.documentId"
            >
              <CIcon name="cil-magnifying-glass" class="mr-1" /> Read details from image
            </CButton>
            <small class="d-block text-muted mt-2">
              Fills in the shop, date and amount for you. Check them before saving.
            </small>
          </CCardFooter>
        </CCard>
      </CCol>
      <CCol lg="4">
        <CCard>
          <CCardHeader><strong>Details</strong></CCardHeader>
          <CCardBody>
            <CForm @submit.prevent="submit">
              <CInput
                label="Shop or company"
                v-model="obj.companyName"
                @input="onCompanyNameChange"
              />
              <CInput
                label="Date and time *"
                type="datetime-local"
                :value="computeBillDate"
                @change="setDateFilter"
              />
              <CInput
                label="Amount *"
                type="number"
                min="0"
                step="0.01"
                inputmode="decimal"
                prepend="RM"
                v-model="obj.totalAmount"
                :is-valid="amountError ? false : null"
                :invalid-feedback="amountError"
              />
            </CForm>
          </CCardBody>
        </CCard>
      </CCol>
    </CRow>
  </div>
</template>

<script>
import ReceiptApi from "../../lib/receiptApi";
import ResitAiApi from "@/lib/resitaiApi";
import DocumentApi from "@/lib/documentApi";

import ChartOfAccountApi from "../../lib/chartOfAccountApi";
import WidgetsUploadImage from "../widgets/WidgetsUploadImage.vue";
import { toApiInstant } from "@/utils/datetime";
import vSelect from "vue-select";
import "vue-select/dist/vue-select.css";

export default {
  name: "Receipt",
  components: {
    WidgetsUploadImage,
    vSelect,
  },
  data: () => {
    return {
      selectedChartOfAccount: null,
      chartOfAccountItems: [],
      isLoading: false, // Track loading state
      isLoadingSearchChartOfAccount: false,
      progressValue: 0, // Track progress
      documentApi: new DocumentApi(),
      saving: false,
      amountError: "",
      uploadedFiles: [],
      obj: {},
      billDateTime: new Date(),
      submitted: false,
      drawType: "",
      drawingState: "",
      api: new ReceiptApi(),
      resitAiApi: new ResitAiApi(),
      // ocrApi: new OcrApi(),
      chartOfAccountApi: new ChartOfAccountApi(),
      loading: false,
    };
  },
  mounted() {
    var self = this;

    self.refreshChartOfAccount();
    self.resetObj();

    this.debounceSearchChartOfAccount = this.debounce(
      this.searchChartOfAccount,
      1000 // Adjust the delay as needed
    );
  },
  computed: {
    billImageUrl() {
      var self = this;
      return apiUrl + "documents/file/" + self.obj.documentId;
    },
    computeBillDate() {
      // Value for <input type="datetime-local">: the browser's wall-clock time.
      const d = this.billDateTime instanceof Date ? this.billDateTime : new Date(this.billDateTime);
      if (isNaN(d.getTime())) return "";
      const pad = (n) => String(n).padStart(2, "0");
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    },
  },

  methods: {
    // Wrap the search function in debounce
    // Debounce function added here
    debounce(func, delay) {
      let timeout;
      return function (...args) {
        clearTimeout(timeout);
        timeout = setTimeout(() => {
          func.apply(this, args);
        }, delay);
      };
    },

    searchChartOfAccount(merchantName) {
      var suggestDto = {
       businessId: this.obj.businessId,
        merchantName: merchantName,
      };

      this.isLoadingSearchChartOfAccount = true;
      this.chartOfAccountApi
        .suggestChartOfAccount(suggestDto)
        .then((response) => {
          this.selectedChartOfAccount = response.result;
        })
        .catch((error) => {
          console.error("Error suggesting chart of account:", error);
        })
        .finally(() => {
          this.isLoadingSearchChartOfAccount = false;
        });
    },

    debounceSearchChartOfAccount: null,
    onCompanyNameChange() {
      // Trigger the debounced search function whenever the company name changes
      this.debounceSearchChartOfAccount(this.obj.companyName);
    },

    refreshChartOfAccount() {
      var self = this;
      self.chartOfAccountApi
        .getListByCurrentBusiness()
        .then((response) => {
          self.chartOfAccountItems = response.result;
        })
        .catch(({ data }) => {});
    },
    extractImageFromUrl(imageUrl) {
      this.isLoading = true;
      this.progressValue = 0; // Reset progress

      const interval = setInterval(() => {
        if (this.progressValue < 95) {
          this.progressValue += 5; // Simulate progress
        }
      }, 500);

      // Fetch the image from the URL
      fetch(imageUrl)
        .then((response) => {
          // Convert the response to a Blob

          return response.blob();
        })
        .then((imageBlob) => {
          // Create a FormData object to send the image Blob and OCR engine choice
          const formData = new FormData();
          formData.append("file", imageBlob, "image.jpg"); // Append the image as a Blob
          formData.append("ocr_engine", "googlevision"); // Append the OCR engine

          // Use your custom API wrapper to make the call
          const resitAiApi = new ResitAiApi();
          return resitAiApi.extract(formData); // Return the API call so we can chain the next .then()
        })
        .then((result) => {
          // Handle the result of the extraction
          this.progressValue = 100; // Complete progress
          this.toast("Success", "Details filled in from the receipt. Check them before saving", "success");

          // Assuming the result contains parsed data, populate the obj fields
          this.obj.companyName = this.getMerchantName(result.parsed_data);
          this.obj.billNo = result.parsed_data.billNo;
          this.billDateTime = this.getDateTime(result.parsed_data);
          this.obj.totalAmount = this.getAmount(result.parsed_data);

          this.debounceSearchChartOfAccount(this.obj.companyName);
          // ... map other fields as necessary
        })
        .catch((error) => {
          // Handle any errors that occurred during the fetch or the API call
          console.error("Error extracting data:", error);
          this.toast("Error", "Couldn't read this receipt. Enter the details yourself", "danger");
        })
        .finally(() => {
          // Optionally, do something after the entire chain is complete, like hiding a loading spinner
          clearInterval(interval);
          this.isLoading = false;
        });
    },
    getDateTime(parsedData) {
      try {
        // Extract the date part (YYYY-MM-DD) and time part (HH:MM)
        const datePart = parsedData.date.split("T")[0]; // Extract just the date portion from the string
        const timePart = parsedData.time; // The time is already in HH:MM format

        // Combine the date and time into a full datetime string
        const dateTimeString = `${datePart}T${timePart}:00`; // Add ":00" for the seconds part if necessary

        // Convert to a JavaScript Date object
        const dateTimeObject = new Date(dateTimeString);
        return dateTimeObject;
      } catch (error) {
        return new Date();
      }
    },
    getAmount(parsedData) {
      var amount = parsedData.amount;
      try {
        if (Array.isArray(amount)) {
          // If it's an array, return the highest value
          return Math.max(...amount);
        } else {
          // If it's a single value, return it directly
          return amount;
        }
      } catch (error) {
        return 0;
      }
    },

    getMerchantName(parsedData) {
      try {
        return parsedData.merchant_name;
      } catch (error) {
        return "";
      }
    },
    extract() {
      this.extractImageFromUrl(this.billImageUrl);
    },

    downloadImage() {},

    uploaded(data) {
      var self = this;
      self.uploadedFiles = data.uploadedFiles;
      if (self.uploadedFiles.length > 0) {
        self.obj.documentId = self.uploadedFiles[0].id;
        // this.loadImage();
      }
    },
    // updateCursor(cursor) {
    //   let stage = this.$refs.stage.getStage();
    //   stage.container().style.cursor = cursor;
    // },
    resetObj() {
      var self = this;
      if (self.$route.params.id) {
        self.api
          .get(self.$route.params.id)
          .then((response) => {
            self.obj = response.result;
            self.obj.totalAmount = (parseFloat(self.obj.totalAmount) || 0).toFixed(2);
            self.billDateTime = self.obj.date;
            self.selectedChartOfAccount = self.obj.chartAccount;
          })
          .catch(({ data }) => {
            self.toast("Error", `Couldn't load the receipt. ${helper.getErrorMessage(data)}`, "danger");
          });
      } else {
        self.obj = self.getEmptyObj();
      }
    },
    setDateFilter(e) {
      this.billDateTime = new Date(e.target.value).getTime();
    },
    onSubmit() {
      var self = this;

      const amount = parseFloat(self.obj.totalAmount);
      if (self.obj.totalAmount === "" || self.obj.totalAmount == null || isNaN(amount) || amount < 0) {
        self.amountError = "Enter the amount on the receipt, for example 25.90.";
        return;
      }
      self.amountError = "";
      if (self.saving) return;
      self.saving = true;
      self.obj.date = toApiInstant(self.billDateTime);
      if (self.selectedChartOfAccount) {
        self.obj.chartAccount = self.selectedChartOfAccount;
        self.obj.chartAccountId = self.selectedChartOfAccount.id;
      } else {
        self.obj.chartAccount = null;
        self.obj.chartAccountId = null;
      }
      // if (self.uploadedFiles.length > 0)
      //   self.obj.documentId = self.uploadedFiles[0].id;

      //self.obj.drawCompany = this.drawCompany;

      if (!self.obj.id) {
        this.api
          .create(self.obj)
          .then((response) => {
            self.obj = response.result;
            self.toast("Success", "Receipt added", "success");
            self.$router.push({ path: `/employee/Receipt/${self.obj.id}` });
          })
          .catch(({ data }) => {
            self.toast("Error", `Couldn't add the receipt. ${helper.getErrorMessage(data)}`, "danger");
          })
          .finally(() => {
            self.saving = false;
          });
      } else {
        this.api
          .update(self.obj)
          .then((response) => {
            self.toast("Success", "Receipt saved", "success");
          })
          .catch(({ data }) => {
            self.toast("Error", `Couldn't save the receipt. ${helper.getErrorMessage(data)}`, "danger");
          })
          .finally(() => {
            self.saving = false;
          });
      }
    },
    onReset() {
      //evt.preventDefault();
      this.obj = {};
    },
    toast(header, message, color) {
      const kind = { danger: "error", success: "success", warning: "warning" }[color] || "info";
      this.$toast[kind](message);
    },

    checkIfValid(fieldName) {
      const field = this.$v.obj[fieldName];
      if (!field.$dirty) {
        return null;
      }
      return !(field.$invalid || field.$model === "");
    },
    validate() {
      this.$v.$touch();
    },
    reset() {
      // this.obj = this.getEmptyObj();
      this.resetObj();
      this.submitted = false;
      this.$v.$reset();
    },
    getEmptyObj() {
      return {
        date: new Date(),
        name: "",
        documentId: "",
        drawCompany: {},
      };
    },
    analyze() {},
    addNew() {
      this.$router.push({ path: "/employee/Receipt" });
    },
    previous() {
      var self = this;
      this.api
        .getPrev(self.obj.id)
        .then((response) => {
          var nextObj = response.result;
          this.$router.push({
            path: `/employee/Receipt/${nextObj.id}`,
          });
        })
        .catch(() => {
          self.toast("Info", "This is your first receipt", "info");
        });
    },
    next() {
      var self = this;
      this.api
        .getNext(self.obj.id)
        .then((response) => {
          var nextObj = response.result;
          this.$router.push({
            path: `/employee/Receipt/${nextObj.id}`,
          });
        })
        .catch(() => {
          self.toast("Info", "This is your latest receipt", "info");
        });
    },
    submit() {
      // if (this.isValid) {
      this.onSubmit();
      this.submitted = true;
      // }
    },
    cancel() {
      this.$router.push({ path: "/employee/receiptList" });
    },

    // onChartOfAccountSelected(item, index) {
    //   var self = this;
    //   self.selectedChartOfAccount = item;
    //   self.obj.chartAccountId = item.id.toString();
    //   self.chartOfAccountSearchPopup = false;
    // },
  },
};
</script>
